import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence, animate, MotionConfig } from 'framer-motion'
import Routes from './components/Routes'
import PageHeader from './components/ui/PageHeader'
import { useLocation } from 'wouter'
import { easing, duration } from '@/lib/animations'
import { Sun, Moon } from 'lucide-react'
import { Button } from '@/components/ui/primitives/button'
import { Head } from '@/components/ui/Head'

function App() {
  const [location, navigate] = useLocation()
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [currentTheme, setCurrentTheme] = useState(() => {
    try {
      return localStorage.getItem('theme') || 'dark'
    } catch (e) {
      return 'dark'
    }
  })

  // Apply theme to document and persist
  useEffect(() => {
    const root = document.documentElement
    const themeClasses = ['dark', 'trans']
    
    // Remove all theme classes
    root.classList.remove(...themeClasses)
    
    // Apply current theme class (only if not 'default')
    if (currentTheme !== 'default') {
      root.classList.add(currentTheme)
    }
    
    // Persist theme selection
    try {
      localStorage.setItem('theme', currentTheme)
    } catch (e) {
      // ignore storage errors
    }
  }, [currentTheme])
  
  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'dark' ? 'default' : 'dark')
  }

  const isStyleguide = location === '/styleguide'

  return (
    <MotionConfig reducedMotion="user">
      <div className={`mx-auto flex flex-col min-h-dvh px-4 sm:px-0 ${isStyleguide ? 'pb-8' : 'py-6 sm:pt-32 sm:pb-8'} ${location === '/' ? 'max-w-3xl items-start' : 'items-start sm:items-center'}`}>
          {!isStyleguide && (
            <PageHeader
              showNav={true}
              onNavigateHome={location !== '/' ? async () => {
                await animate(window.scrollY, 0, {
                  duration: duration.normal,
                  ease: easing.outQuad,
                  onUpdate: (latest) => {
                    window.scrollTo(0, latest)
                  }
                })
                navigate('/')
              } : undefined}
            />
          )}
          {location === '/' && <Head />}
          <div className="flex-1 w-full">
            <Routes onLightboxChange={setIsLightboxOpen} />
          </div>
          
          {!isStyleguide && (
            <footer className="mt-16 sm:mt-24 pb-4 w-full max-w-3xl">
              <div className="flex items-center justify-center gap-4">
                <p className="text-xs text-muted-foreground/60">
                  All the words on this website have been written by a human being. 
                </p>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  aria-label={currentTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  className="hover-transitions flex-shrink-0"
                >
                  {currentTheme === 'dark' ? (
                    <Sun className="h-4 w-4" />
                  ) : (
                    <Moon className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </footer>
          )}
      </div>
    </MotionConfig>
  )
}

export default App


